import React, { useState, useEffect, useRef, useMemo } from "react"
import ReactTable from "react-table"
import withFixedColumns from "react-table-hoc-fixed-columns"
import ExcelExport from "components/ExcelExport"
import { storePref, checkPref } from "helpers/pref"
import { storeSortPref, checkSortPref } from "helpers/sortPref"
import { storeFilterPref, checkFilterPref } from "helpers/filterPref"
import moment from "moment"
import _ from "lodash"
import helpers from "helpers/helpers"

const ReactTableFixedColumns = withFixedColumns(ReactTable)

const FullTable = (props) => {
	const [columns, setColumns] = useState([])
	const [sorted, setSorted] = useState([])
	const [toggleShow, setToggleShow] = useState(false)
	const [filtered, setFiltered] = useState([])
	const [ready, setReady] = useState(true)

	const tableRef = useRef(null)
	const wrapperRef = useRef(null)
	const innerTop = useRef(null)
	const topWrapper = useRef(null)
	// The observer is for the top horizontal bars.  Have to monitor the rt-table scrollWidth, then set it to the inner so the bar appears on load.
	const headObserver = new MutationObserver((mutationrecords) => {
		try {
			innerTop.current.style.width = `${wrapperRef.current.querySelector(".rt-table").scrollWidth}px`
		} catch (e) {
			console.log(e)
		}
	})

	useEffect(() => {
		init()
	}, [])

	useEffect(() => {
		if (props.reInit && !_.isEqual(props, props.prev)) {
			init()
		}
		setupListeners()
		return () => {
			const tableBody = wrapperRef.current?.querySelector(".rt-table")
			tableBody.removeEventListener("scroll", scrollEvent)
			topWrapper?.current?.removeEventListener("scroll", scrollEvent)
		}
	}, [props, props.reInit])

	const setupListeners = () => {
		const tableBody = wrapperRef.current?.querySelector(".rt-table")
		if (tableBody) {
			tableBody.addEventListener("scroll", scrollEvent)
			topWrapper.current.addEventListener("scroll", scrollEvent)
			innerTop.current.style.width = `${tableBody.scrollWidth}px`
		}
	}

	useEffect(() => {
		if (!ready) {
			init()
		}
	}, [ready])

	const scrollEvent = (e) => {
		try {
			let scrolledElement = e.target.className
			if (scrolledElement?.includes("rt-table")) {
				let horiz = wrapperRef.current.querySelector(".rt-table").scrollLeft
				topWrapper.current.scrollLeft = horiz
				innerTop.current.style.width = `${wrapperRef.current.querySelector(".rt-table").scrollWidth}px`
			} else if (scrolledElement?.includes("horizontalBar")) {
				let horiz = topWrapper.current.scrollLeft
				wrapperRef.current.querySelector(".rt-table").scrollLeft = horiz
				innerTop.current.style.width = `${wrapperRef.current.querySelector(".rt-table").scrollWidth}px`
			}
		} catch (e) {
			console.log(e)
		}
	}

	const init = () => {
		let newColumns = _.cloneDeep(props.columns)
		let uwLookup = localStorage.getItem("columnWidths")
		let userWidths = uwLookup ? JSON.parse(uwLookup) : null
		let pref = checkPref(props.name)

		_.forEach(newColumns, (v) => {
			if (!v.hasOwnProperty("display")) {
				v.display = true
			}
			if (userWidths && userWidths[props.name]) {
				let lookup = _.find(userWidths[props.name], { id: v.accessor })
				if (lookup) v.width = lookup.value
			}
		})

		if (pref) {
			_.forEach(pref, (v, k) => {
				let f = _.findIndex(newColumns, { accessor: k })
				if (newColumns[f]) newColumns[f].display = v
			})
		}

		let newFiltered
		let filteredCheck = checkFilterPref(props.name)
		if (filteredCheck) newFiltered = filteredCheck

		setColumns(newColumns)
		setFiltered(newFiltered)
		setReady(true)
		if (wrapperRef?.current?.querySelector) {
			headObserver.observe(wrapperRef.current?.querySelector(".rt-thead"), { attributes: true })
		}
	}

	const handleToggleShow = () => {
		setToggleShow(!toggleShow)
	}

	const handlePrefStore = (type, newColumns) => {
		let storage = {}
		for (let v of newColumns) {
			storage[v.accessor] = v.display
		}
		storePref(props.name, storage)
	}

	const checkboxChange = (e, i) => {
		let newColumns = _.cloneDeep(columns)
		let index = typeof i === "number" ? i : _.findIndex(newColumns, { name: e.target.Header })
		newColumns[index].display = !newColumns[index].display
		handlePrefStore(props.name, newColumns)
		setColumns(newColumns)
	}

	const renderFilters = () => {
		let checkboxes = []
		_.forEach(columns, (v) => {
			if (v.Header === "" || v.disableFilter) return
			let index = _.findIndex(columns, { accessor: v.accessor })
			if (index === -1) return
			checkboxes.push(
				<li
					className="filterCheckboxes__item"
					key={v.Header}
					data-value={v.Header}
					onClick={() => checkboxChange(null, index)}>
					<input
						type="checkbox"
						name={v.Header}
						value={v.Header}
						checked={columns[index].display}
						onChange={() => checkboxChange(null, index)}
					/>
					{v.name ? v.name : v.Header}
				</li>
			)
		})
		return checkboxes
	}

	const displayColumns = () => {
		return _.filter(columns, { display: true })
	}

	const checkAll = (value) => {
		let state = _.cloneDeep(columns)
		_.forEach(state, (v, k) => {
			state[k].display = value
		})
		storePref(props.name, state)
		setColumns(state)
	}

	const storeColumnWidth = (name, opts) => {
		let current = localStorage.getItem("columnWidths") ? localStorage.getItem("columnWidths") : {}
		if (!_.isEmpty(current)) {
			current = JSON.parse(current)
		}
		if (current[name]) {
			// already exists, add to
			current[name].unshift(...opts)
			current[name] = _.uniqBy(current[name], "id")
		} else current[name] = opts

		localStorage.setItem("columnWidths", JSON.stringify(current))
	}

	const removeWidthPrefs = (name) => {
		let current = localStorage.getItem("columnWidths") ? localStorage.getItem("columnWidths") : {}
		if (!_.isEmpty(current)) {
			current = JSON.parse(current)
		}
		if (current[name]) delete current[name]
		localStorage.setItem("columnWidths", JSON.stringify(current))
		/* setReady(false)
		setState({ ready: false }, () => {
			init()
			setState({ ready: true })
		}) */
		setReady(false)
	}

	const determineSortOrder = () => {
		// First if the user has a preference:
		if (checkSortPref(props.name)) {
			return checkSortPref(props.name)
		} else if (props.defaultSorted) {
			return props.defaultSorted
		} else return []
	}

	const passUpSorted = () => {
		//? This watches for events like sorting, adding filters, etc and fires if the props has a onTableStateChange - originally used in lot management.  It will send up the tableref so you can do whatever you want with it on the parent. tableRef.getResolvedState().sortedData will give you the sorted data.
		try {
			if (props?.onTableStateChange && sorted[0]) {
				props.onTableStateChange(tableRef.current)
			}
		} catch (e) {
			console.log(e)
		}
	}

	const buildTable = () => {
		return (
			<div className="fullTable">
				<ExcelExport
					data={sorted}
					excelClean={props.excelClean}
					index={0}
					unset={props.unset || []}
					title={props.title}
					columns={displayColumns()}
					allColumns={props.columns}
					exportAllColumns={props.exportAllColumns}
					appendedText={props.appendedText}
					rowGroups={props.rowGroups || null}
					leavePercents={props.leavePercents || null}
					tableRef={tableRef.current}
					hasColumnsGrouped={props.hasColumnsGrouped || null}
				/>
				<div ref={wrapperRef} className="w-full h-full">
					<ReactTableFixedColumns
						innerRef={(ref) => {
							tableRef.current = ref
						}}
						data={props.data}
						columns={displayColumns()}
						filterable
						loadingText=""
						sortable={props.sortable}
						defaultPageSize={props.pageSize || 10}
						className={props.classNames}
						defaultSorted={determineSortOrder()}
						defaultFiltered={filtered || []}
						onResizedChange={(r) => {
							storeColumnWidth(props.name, r)
						}}
						onSortedChange={(newSorted, column, shiftKey) => {
							storeSortPref(props.name, newSorted)
							passUpSorted()
						}}
						getTrProps={
							props.getTrProps ||
							function () {
								return {}
							}
						}
						ref={(r) => {
							sorted[0] = r
							passUpSorted()
						}}
						filtered={filtered}
						onFilteredChange={(newFiltered, v) => {
							newFiltered = _.map(newFiltered, (v) => {
								return { ...v, value: _.trimStart(v.value) }
							})
							storeFilterPref(props.name, newFiltered)
							setFiltered(newFiltered)
							passUpSorted()
						}}>
						{(state, makeTable) => {
							let totalRecords = ""
							const { sortedData } = state
							if (sortedData && sortedData.length > 0) {
								totalRecords = sortedData.length
							} else totalRecords = 0
							return (
								<>
									<p className="rowCount">
										Number of Rows: {totalRecords.toLocaleString()} | Page: {state.page + 1} |
										Viewing Records : {state.startRow + 1} - {state.endRow}
										{props.dateOf && ` | Date of Report: ${moment().format("MM/DD/YYYY")}`}
									</p>
									<div className="pointer clearFilters clearFilters--duo">
										<div>
											<div className="titleFilter">
												<p className="filterCheck">
													<span onClick={() => checkAll(true)}>Check All</span>
													<span onClick={() => checkAll(false)}>Uncheck All</span>
												</p>
											</div>
											<div className="filterCheckboxes__cont pt-2">
												<div
													className="filterCheckboxes__toggle"
													onClick={() => handleToggleShow()}
													data-html2canvas-ignore="true">
													{toggleShow ? "Hide" : "Show"} Filters
												</div>
												{toggleShow && (
													<ul className="filterCheckboxes" data-html2canvas-ignore="true">
														{renderFilters()}
													</ul>
												)}
											</div>
										</div>
										<div style={{ fontSize: 11 }}>
											<a
												onClick={() => {
													removeWidthPrefs(props.name)
												}}>
												<i className="fas fa-times-circle"></i> Reset Widths
											</a>
											<a
												onClick={() => {
													storeFilterPref(props.name, [])
													setFiltered([])
												}}
												style={{ marginLeft: 15 }}>
												<i className="fas fa-times-circle"></i> Clear Text
											</a>
										</div>
									</div>
									<div className="horizontalBar" ref={topWrapper}>
										<div className="horizontalBar__bar" ref={innerTop}></div>
									</div>
									{makeTable()}
								</>
							)
						}}
					</ReactTableFixedColumns>
				</div>
			</div>
		)
	}

	if (ready) {
		return buildTable()
	} else {
		return ""
	}
}

export default FullTable
